import {
  readPersistedAuth,
  writePersistedAuthTokens,
} from '@/store/authTokensSync';
import { API_AUTH_URL } from '@/utils';

const AUTH_REFRESH_LOCK = 'auth-refresh';

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
}

export type RefreshOutcome = 'refreshed' | 'invalid' | 'unavailable';

/**
 * Margen con el que se considera que un access token ya no sirve: si le queda
 * menos que esto, se refresca antes de usarlo.
 */
const EXPIRY_MARGIN_MS = 60_000;

const decodeExpiry = (token: string): number | null => {
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '='
    );
    const exp = JSON.parse(atob(padded))?.exp;
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
};

/**
 * Si el access token todavía está vigente no hay nada que refrescar.
 *
 * Importa porque el refresh token es de un solo uso: pedir uno nuevo en cada
 * carga de pestaña quemaba el que las otras pestañas todavía tenían guardado, y
 * cualquier tropiezo en esa rotación terminaba en la pantalla de login. Sin
 * `exp` legible se responde que no está fresco, así que se refresca igual: el
 * comportamiento viejo queda como piso.
 */
export const isAccessTokenFresh = (token: string | null | undefined): boolean => {
  if (!token) return false;
  const expiresAt = decodeExpiry(token);
  return expiresAt !== null && expiresAt - Date.now() > EXPIRY_MARGIN_MS;
};

interface EnsureFreshTokensOptions {
  staleAccessToken: string | null;
  getStoreTokens: () =>
    | { accessToken?: string | null; refreshToken?: string | null }
    | undefined;
  onNewTokens: (tokens: TokenPair) => void;
}

let fallbackMutex: Promise<unknown> = Promise.resolve();

const withRefreshLock = <T>(fn: () => Promise<T>): Promise<T> => {
  const locks = (navigator as any)?.locks;
  if (locks?.request) {
    return locks.request(AUTH_REFRESH_LOCK, fn) as Promise<T>;
  }
  const run = fallbackMutex.then(fn, fn);
  fallbackMutex = run.then(
    () => undefined,
    () => undefined
  );
  return run;
};

const requestNewTokens = async (
  refreshToken: string
): Promise<{ tokens: TokenPair } | { outcome: 'invalid' | 'unavailable' }> => {
  try {
    const response = await fetch(`${API_AUTH_URL}/auth/refresh`, {
      method: 'POST',
      headers: { refresh_token: refreshToken },
    });
    /**
     * Solo un rechazo explícito (401/400) da la sesión por muerta. Cualquier
     * 5xx es un tropiezo del servidor, no una respuesta sobre la sesión: antes
     * el 500 —que es lo que devolvía este endpoint ante un refresh token
     * vencido, y también ante cualquier error interno— caía en 'invalid' y
     * deslogueaba al usuario en el acto.
     */
    if (!response.ok) {
      return { outcome: response.status >= 500 ? 'unavailable' : 'invalid' };
    }
    const data = await response.json();
    if (!data?.accessToken || !data?.refreshToken) {
      return { outcome: 'invalid' };
    }
    return {
      tokens: {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
      },
    };
  } catch {
    return { outcome: 'unavailable' };
  }
};

export const ensureFreshTokens = ({
  staleAccessToken,
  getStoreTokens,
  onNewTokens,
}: EnsureFreshTokensOptions): Promise<RefreshOutcome> =>
  withRefreshLock(async () => {
    const storeTokens = getStoreTokens();
    if (
      storeTokens?.accessToken &&
      storeTokens.accessToken !== staleAccessToken
    ) {
      return 'refreshed';
    }

    const persisted = readPersistedAuth();
    if (
      persisted?.accessToken &&
      persisted.refreshToken &&
      persisted.accessToken !== staleAccessToken
    ) {
      onNewTokens({
        accessToken: persisted.accessToken,
        refreshToken: persisted.refreshToken,
      });
      return 'refreshed';
    }

    const refreshToken = storeTokens?.refreshToken || persisted?.refreshToken;
    if (!refreshToken) return 'invalid';

    const result = await requestNewTokens(refreshToken);
    if ('outcome' in result) {
      if (result.outcome === 'invalid') {
        const latest = readPersistedAuth();
        if (
          latest?.accessToken &&
          latest.refreshToken &&
          latest.accessToken !== staleAccessToken
        ) {
          onNewTokens({
            accessToken: latest.accessToken,
            refreshToken: latest.refreshToken,
          });
          return 'refreshed';
        }
      }
      return result.outcome;
    }

    writePersistedAuthTokens(result.tokens);
    onNewTokens(result.tokens);
    return 'refreshed';
  });

export const tryRefreshTokens = ({
  getStoreTokens,
  onNewTokens,
}: {
  getStoreTokens: () => {
    accessToken?: string | null;
    refreshToken?: string | null;
  } | undefined;
  onNewTokens: (tokens: TokenPair) => void;
}): Promise<RefreshOutcome> =>
  withRefreshLock(async () => {
    const refreshToken = getStoreTokens()?.refreshToken;
    const persisted = readPersistedAuth();

    /**
     * El refresh token es de un solo uso: el backend lo rota y deja el anterior
     * inservible. Si mientras esperábamos el lock otra pestaña (o un segundo
     * montaje de este mismo efecto) ya lo rotó, reusar el viejo devuelve 401 y
     * tira la sesión abajo. En ese caso adoptamos el token nuevo sin pegarle a
     * la red. Es el mismo resguardo que ya tenía ensureFreshTokens.
     */
    if (
      persisted?.accessToken &&
      persisted.refreshToken &&
      persisted.refreshToken !== refreshToken
    ) {
      onNewTokens({
        accessToken: persisted.accessToken,
        refreshToken: persisted.refreshToken,
      });
      return 'refreshed';
    }

    const usableToken = refreshToken || persisted?.refreshToken;
    if (!usableToken) return 'invalid';

    // El access token viejo va como segundo argumento. Faltaba, asi que
    // `onNewTokens` entraba en su lugar y el callback quedaba `undefined`:
    // adentro, la comparacion `latest.accessToken !== staleAccessToken` daba
    // siempre true (string contra funcion), entraba en la rama de adopcion e
    // invocaba `undefined(...)`. Eso reventaba con "is not a function" en una
    // promesa sin catch y dejaba TODA la app en blanco, en los dos paises,
    // cada vez que se ejercitaba el refresh.
    const staleAccessToken =
      getStoreTokens()?.accessToken ?? persisted?.accessToken ?? null;

    return await requestNewTokensAndUpdate(
      usableToken,
      staleAccessToken,
      onNewTokens
    );
  });

const requestNewTokensAndUpdate = async (
  refreshToken: string,
  staleAccessToken: string | null,
  onNewTokens: (tokens: TokenPair) => void
): Promise<RefreshOutcome> => {
  const result = await requestNewTokens(refreshToken);
  if ('outcome' in result) {
    if (result.outcome === 'invalid') {
      const latest = readPersistedAuth();
      if (
        latest?.accessToken &&
        latest.refreshToken &&
        latest.accessToken !== staleAccessToken
      ) {
        onNewTokens({
          accessToken: latest.accessToken,
          refreshToken: latest.refreshToken,
        });
        return 'refreshed';
      }
    }
    return result.outcome;
  }

  writePersistedAuthTokens(result.tokens);
  onNewTokens(result.tokens);
  return 'refreshed';
};
